import {
  YSTNJ_PARENT_COGNITO_GROUPS,
  YSTNJ_REVIEW_BYPASS_COGNITO_GROUPS,
  YSTNJ_REVIEW_GROUP_BY_MODULE,
  YSTNJ_REVIEW_MODULE_IDS,
  YSTNJ_REVIEW_ROUTE_ROOT_BY_MODULE,
  parseCanonicalReviewModuleId,
  type YstnjCognitoGroup,
  type YstnjReviewModuleId,
  normalizeYstnjVocabularyToken,
} from './ystnjCognitoGroupVocabulary';
import { normalizeCanonicalOrMigrationCognitoGroups } from './ystnjCognitoGroupVocabularyCompat';
import { resolveStableCognitoGroupsForAuth } from './ystnjDomainEntitlements';

export type ReviewScopedModuleId = YstnjReviewModuleId;

export const REVIEW_SCOPED_MODULE_IDS = [...YSTNJ_REVIEW_MODULE_IDS];

export const REVIEW_GROUP_BY_MODULE = { ...YSTNJ_REVIEW_GROUP_BY_MODULE };

export const REVIEW_ROUTE_ROOT_BY_MODULE = {
  ...YSTNJ_REVIEW_ROUTE_ROOT_BY_MODULE,
};

export const REVIEW_BYPASS_GROUPS = [...YSTNJ_REVIEW_BYPASS_COGNITO_GROUPS];
const REVIEW_BYPASS_GROUP_SET = new Set<string>(REVIEW_BYPASS_GROUPS);

export const REVIEW_PARENT_GROUPS = [...YSTNJ_PARENT_COGNITO_GROUPS];
const REVIEW_PARENT_GROUP_SET = new Set<string>(REVIEW_PARENT_GROUPS);

export const normalizeReviewGroupToken = normalizeYstnjVocabularyToken;

export const normalizeReviewGroups = (
  groups: readonly string[] | null | undefined,
): YstnjCognitoGroup[] => normalizeCanonicalOrMigrationCognitoGroups(groups);

export const isReviewScopedModuleId = (
  value: string | null | undefined,
): value is ReviewScopedModuleId =>
  Boolean(parseCanonicalReviewModuleId(value));

export const resolveRequiredReviewGroupForModule = (
  moduleId: string | null | undefined,
): string | null => (
  isReviewScopedModuleId(moduleId)
    ? REVIEW_GROUP_BY_MODULE[moduleId]
    : null
);

export const resolveRouteRootForModule = (
  moduleId: string | null | undefined,
): string | null => (
  isReviewScopedModuleId(moduleId)
    ? REVIEW_ROUTE_ROOT_BY_MODULE[moduleId]
    : null
);

export const hasReviewBypassAccess = (
  groups: readonly string[] | null | undefined,
): boolean => {
  const normalizedGroups = normalizeReviewGroups(groups);
  return normalizedGroups.some((group) => REVIEW_BYPASS_GROUP_SET.has(group));
};

export const hasParentGroupAccess = (
  groups: readonly string[] | null | undefined,
): boolean => {
  const normalizedGroups = normalizeReviewGroups(groups);
  return normalizedGroups.some((group) => REVIEW_PARENT_GROUP_SET.has(group));
};

export const hasModuleReviewAccess = (
  groups: readonly string[] | null | undefined,
  moduleId: string | null | undefined,
): boolean => {
  if (!isReviewScopedModuleId(moduleId)) {
    return true;
  }

  if (hasReviewBypassAccess(groups)) {
    return true;
  }

  const stableGroups = resolveStableCognitoGroupsForAuth(groups ?? []);
  if (
    stableGroups.includes('staff')
    || stableGroups.includes('admin')
    || stableGroups.includes('super-administrator')
  ) {
    return true;
  }

  const normalizedGroups = normalizeReviewGroups(groups);
  const requiredGroup = REVIEW_GROUP_BY_MODULE[moduleId];
  return normalizedGroups.includes(requiredGroup);
};

export const resolveAuthorizedReviewModules = (
  groups: readonly string[] | null | undefined,
): ReviewScopedModuleId[] =>
  REVIEW_SCOPED_MODULE_IDS.filter((moduleId) => hasModuleReviewAccess(groups, moduleId));

export const resolveReviewAccessDeniedMessage = (
  moduleId: ReviewScopedModuleId,
  options?: {
    pathname?: string | null;
  },
): string => {
  const requiredGroup = REVIEW_GROUP_BY_MODULE[moduleId];
  const pathname = String(options?.pathname ?? '').trim();
  const pathSuffix = pathname ? ` at "${pathname}"` : '';
  return (
    `[YSTNJ auth] Module "${moduleId}" requires stable staff-lane Cognito access`
    + (requiredGroup ? ` or legacy compatibility group "${requiredGroup}"` : '')
    + `${pathSuffix}. Authorization decisions for this surface rely exclusively on cognito:groups.`
  );
};

export const resolveReviewScopedModuleFromPath = (
  pathname: string | null | undefined,
): ReviewScopedModuleId | null => {
  const normalizedPathname = String(pathname ?? '').trim().split('?')[0] ?? '';
  for (const moduleId of REVIEW_SCOPED_MODULE_IDS) {
    const routeRoot = REVIEW_ROUTE_ROOT_BY_MODULE[moduleId];
    if (
      normalizedPathname === routeRoot
      || normalizedPathname.startsWith(`${routeRoot}/`)
    ) {
      return moduleId;
    }
  }
  return null;
};
